// ============================================================================
//  Header.tsx — NexusCorp ERP · UI Modernisation · Linear-quality
//  Clean header · Raleway · Refined micro-interactions
// ============================================================================
import { Search, Bell, ChevronDown, LogOut, User as UserIcon, Menu, Settings } from 'lucide-react'
import { useState } from 'react'
import { useAuthStore } from '@/stores/auth.store'
import { useUIStore } from '@/stores/ui.store'
import { useNavigate, useLocation } from 'react-router-dom'
import NotificationDropdown from './NotificationDropdown'

/* ── Breadcrumb helper ───────────────────────────────────── */
const ROUTE_LABELS: Record<string, string> = {
  dashboard: 'Tableau de bord',
  accounting: 'Comptabilité',
  treasury: 'Trésorerie',
  hr: 'Ressources Humaines',
  projects: 'Projets',
  chat: 'Chat',
  documents: 'Documents',
  meetings: 'Réunions',
  notes: 'Notes',
  crm: 'CRM',
  tickets: 'Tickets',
  orgchart: 'Organigramme',
  invoices: 'Facturation',
  'attendance-enriched': 'Pointage Enrichi',
  admin: 'Administration',
  profile: 'Mon profil',
  imports: 'Imports',
}

function useBreadcrumb() {
  const { pathname } = useLocation()
  const parts = pathname.split('/').filter(Boolean)
  if (parts.length === 0) return []
  const crumbs: string[] = []
  // First meaningful segment
  const first = parts[0]
  crumbs.push(ROUTE_LABELS[first] ?? first.charAt(0).toUpperCase() + first.slice(1).replace(/-/g, ' '))
  // Second segment if it's a known label (not an ID)
  if (parts[1] && ROUTE_LABELS[parts[1]] && parts[1] !== parts[0]) {
    crumbs.push(ROUTE_LABELS[parts[1]])
  } else if (parts[1] && !/^[a-z0-9]{20,}$/i.test(parts[1])) {
    crumbs.push(parts[1].charAt(0).toUpperCase() + parts[1].slice(1).replace(/-/g, ' '))
  }
  return crumbs
}

export default function Header() {
  const { user, logout } = useAuthStore()
  const { setSidebarOpen } = useUIStore()
  const [showMenu, setShowMenu] = useState(false)
  const navigate = useNavigate()
  const crumbs = useBreadcrumb()

  return (
    <header className="h-[54px] flex items-center px-5 gap-3 shrink-0 bg-white border-b border-[var(--bd)] shadow-[0_1px_0_rgba(15,12,29,0.03)]">
      {/* Mobile hamburger */}
      <button onClick={() => setSidebarOpen(true)}
        className="lg:hidden p-1.5 rounded-lg border-0 bg-transparent cursor-pointer text-[var(--tx3)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)] transition-all flex items-center justify-center">
        <Menu size={18} />
      </button>

      {/* Breadcrumb */}
      <div className="hidden sm:flex items-center gap-1 text-[13px] min-w-0">
        {crumbs.map((c, i) => (
          <span key={i} className="flex items-center gap-1 min-w-0">
            {i > 0 && <span className="text-[var(--tx4)]">/</span>}
            <span className={i === crumbs.length - 1 ? 'text-[var(--tx)] font-semibold truncate' : 'text-[var(--tx3)] truncate'}>
              {c}
            </span>
          </span>
        ))}
      </div>

      {/* Search */}
      <button onClick={() => document.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', metaKey: true, bubbles: true }))}
        className="flex items-center gap-2 w-[220px] px-2.5 h-8 bg-[var(--surface-2)] border border-[var(--bd)] rounded-lg cursor-pointer text-[var(--tx3)] hover:border-[var(--blue)] hover:bg-white hover:shadow-[0_0_0_3px_rgba(245,124,0,0.08)] transition-all ml-auto">
        <Search size={13} className="shrink-0" />
        <span className="flex-1 text-left text-[12.5px] font-medium">Rechercher…</span>
        <kbd className="text-[9px] px-1.5 py-px rounded bg-white border border-[var(--bd)] text-[var(--tx4)] font-mono leading-relaxed">⌘K</kbd>
      </button>

      {/* Right actions */}
      <div className="flex items-center gap-0.5">
        {/* Notifications */}
        <NotificationDropdown />

        {/* Settings */}
        <button onClick={() => navigate('/admin/settings')}
          className="w-8 h-8 rounded-lg border-0 bg-transparent flex items-center justify-center cursor-pointer text-[var(--tx3)] hover:bg-[var(--surface-2)] hover:text-[var(--tx)] transition-all">
          <Settings size={15} />
        </button>

        {/* Divider */}
        <div className="w-px h-5 bg-[var(--bd)] mx-1.5" />

        {/* User menu */}
        <div className="relative">
          <button onClick={() => setShowMenu(v => !v)}
            className={`flex items-center gap-2 py-1 pl-1 pr-2 rounded-lg border cursor-pointer transition-all ${showMenu ? 'bg-[var(--surface-2)] border-[var(--bd)]' : 'bg-transparent border-transparent hover:bg-[var(--surface-2)] hover:border-[var(--bd)]'}`}>
            <div className="w-7 h-7 rounded-full shrink-0 flex items-center justify-center text-[10px] font-bold text-white"
              style={{ background: 'linear-gradient(135deg, #7C3AED, #4B0082)' }}>
              {user?.firstName?.[0]}{user?.lastName?.[0]}
            </div>
            <div className="hidden sm:block text-left min-w-0">
              <p className="text-[12px] font-semibold text-[var(--tx)] leading-tight truncate max-w-[100px]">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-[10px] text-[var(--tx3)] leading-tight truncate">{user?.roles?.[0]}</p>
            </div>
            <ChevronDown size={11} className={`text-[var(--tx4)] transition-transform duration-200 ${showMenu ? 'rotate-180' : ''}`} />
          </button>

          {showMenu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setShowMenu(false)} />
              <div className="absolute right-0 top-[calc(100%+4px)] z-20 w-[200px] rounded-xl overflow-hidden bg-white border border-[var(--bd)] shadow-[0_8px_30px_rgba(15,12,29,0.12),0_2px_8px_rgba(15,12,29,0.05)] nx-scale-in">
                <div className="px-3.5 py-3 border-b border-[var(--bd)]">
                  <p className="text-[13px] font-semibold text-[var(--tx)] leading-tight">{user?.firstName} {user?.lastName}</p>
                  <p className="text-[11px] text-[var(--tx3)] mt-0.5 truncate">{user?.email}</p>
                </div>
                <div className="py-1">
                  <button onClick={() => { navigate('/profile'); setShowMenu(false) }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 border-0 bg-transparent cursor-pointer text-[13px] font-medium text-[var(--tx2)] hover:bg-[var(--surface-2)] transition-colors text-left">
                    <UserIcon size={13} className="text-[var(--blue)]" /> Mon profil
                  </button>
                  <button onClick={async () => { await logout(); navigate('/login') }}
                    className="w-full flex items-center gap-2.5 px-3.5 py-2 border-0 bg-transparent cursor-pointer text-[13px] font-medium text-[var(--red)] hover:bg-[var(--red-bg)] transition-colors text-left">
                    <LogOut size={13} /> Déconnexion
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  )
}
